import axios from "axios";

import { env } from "@/config/env";
import {
  API_ERROR_CODES,
  isJwtAuthErrorCode,
  type ApiErrorBody,
} from "@/lib/api/api-error";
import { redirectToAdminLoginHashRoute } from "@/lib/api/admin-auth-login-redirect";
import {
  clearAdminAuthSession,
  getAdminAccessToken,
} from "@/lib/auth/admin-session";

const PUBLIC_ADMIN_AUTH_API_PATHS = [
  env.adminAuthLoginPath,
  env.adminAuthForgotPasswordPath,
  env.adminAuthResetPasswordPath,
] as const;

export function isPublicAdminAuthApiRequest(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return PUBLIC_ADMIN_AUTH_API_PATHS.some((path) => url.includes(path));
}

export function shouldHandleAdminJwtAuthFailure(
  status: number | undefined,
  code: string | undefined,
  requestUrl: string | undefined,
): boolean {
  if (status === 403) {
    return !isPublicAdminAuthApiRequest(requestUrl);
  }

  if (status !== 401 || !isJwtAuthErrorCode(code)) {
    return false;
  }

  if (
    code === API_ERROR_CODES.jwtAuthenticationRequired &&
    !getAdminAccessToken()
  ) {
    return false;
  }

  return !isPublicAdminAuthApiRequest(requestUrl);
}

const adminApiClient = axios.create({
  baseURL: env.apiUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

adminApiClient.interceptors.request.use(
  (config) => {
    const token = getAdminAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

adminApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data as ApiErrorBody | undefined;
      const code = data?.code;
      const requestUrl = error.config?.url;

      if (shouldHandleAdminJwtAuthFailure(status, code, requestUrl)) {
        clearAdminAuthSession();
        redirectToAdminLoginHashRoute();
      }
    }

    return Promise.reject(error);
  },
);

export default adminApiClient;
